import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../store/auth";
import { parseUiPrefs, useMeta } from "../hooks";
import { useToast } from "../store/toast";
import { useTaskSocket } from "../api/ws";
import PageHeader from "../components/PageHeader";
import KanbanBoard from "../components/KanbanBoard";
import TaskListView, { groupForTimeline } from "../components/TaskListView";
import { ViewPrefsButton, getViewPref, applyViewPref } from "../components/ViewPrefs";
import GanttTimeline from "../components/GanttTimeline";
import TaskModal from "../components/TaskModal";
import TaskDetailPanel from "../components/TaskDetailPanel";
import Paginacion, { usePaginacion } from "../components/Paginacion";
import { EmptyState } from "../components/ui";
import { PRIORITY, STATUS } from "../constants";
import { exportTasksCsv, fmtDate, parseLocal } from "../utils";
import Icon from "../components/Icon";

function cutoffDate(days = 45) {
  const d = new Date(); d.setDate(d.getDate() - days); return d;
}
const PROJECT_CONCLUDED = ["completed", "cancelled"];

function recentPeriods() {
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const f = (d) => d.toLocaleDateString("es-VE", { day: "2-digit", month: "short" });
  const out = [];
  let end = new Date();
  for (let i = 0; i < 8; i++) {
    const start = new Date(end); start.setDate(start.getDate() - 6);
    out.push({ group: "Semanas", from: iso(start), to: iso(end), label: `${f(start)} – ${f(end)}` });
    end = new Date(start); end.setDate(end.getDate() - 1);
  }
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const first = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ group: "Meses", from: iso(first), to: iso(new Date(now.getFullYear(), now.getMonth() - i + 1, 0)), label: first.toLocaleDateString("es-VE", { month: "long", year: "numeric" }) });
  }
  return out;
}

export function ViewSwitcher({ view, setView }) {
  const opts = [["kanban", "Kanban", "grid"], ["list", "Lista", "list"], ["timeline", "Timeline", "chart"]];
  return (
    <div className="flex rounded-xl border border-slate-300 p-0.5 text-sm">
      {opts.map(([k, label, icon]) => (
        <button key={k} onClick={() => setView(k)}
          className={`flex items-center gap-1.5 rounded px-3 py-1 ${view === k ? "bg-brand-600 text-white" : "text-slate-600"}`}>
          <Icon name={icon} size={15} />{label}
        </button>
      ))}
    </div>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const { departments, users, projects, reloadProjects } = useMeta();
  const { push } = useToast();
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState(() => parseUiPrefs(user).default_view);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [priorityF, setPriorityF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [projectF, setProjectF] = useState("");
  const [qualityF, setQualityF] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [hideOldDone, setHideOldDone] = useState(true);
  const [period, setPeriod] = useState(null); // {from,to} | null
  const periods = useMemo(() => recentPeriods(), []);
  const importRef = useRef(null);
  const activeFilters = [priorityF, statusF, typeF, projectF, qualityF].filter(Boolean).length + (period ? 1 : 0);

  const load = useCallback(() => {
    api.get("/tasks", { params: { mine: true } }).then((r) => setTasks(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cutoff = useMemo(() => cutoffDate(user?.old_task_days || 45), [user?.old_task_days]);
  const filtered = useMemo(() => tasks.filter((t) => {
    // A task in a project that isn't concluded yet stays visible regardless of age —
    // "antigua" only makes sense once its project is done.
    if (hideOldDone && t.status === "completed" && (!t.project_id || PROJECT_CONCLUDED.includes(t.project_status))) {
      const d = t.completed_date || t.updated_at;
      if (d && new Date(d) < cutoff) return false;
    }
    if (period) {
      const ref = t.due_date || t.start_date || t.completed_date;
      if (!ref || ref < period.from || ref > period.to) return false;
    }
    return (!priorityF || t.priority === priorityF) &&
      (!statusF || t.status === statusF) &&
      (!typeF || (t.task_type || "") === typeF) &&
      (!projectF || String(t.project_id) === projectF) &&
      (!qualityF || (qualityF === "yes" ? t.quality_verified : !t.quality_verified)) &&
      (!search || `${t.code || ""} ${t.title}`.toLowerCase().includes(search.toLowerCase()));
  }), [tasks, priorityF, statusF, typeF, projectF, qualityF, search, hideOldDone, cutoff, period]);
  const taskTypes = useMemo(() => [...new Set(tasks.map((t) => t.task_type).filter(Boolean))].sort(), [tasks]);

  // Vista preferida del usuario (Mi vista): filtro de completados + multi-orden.
  const viewPref = useMemo(() => getViewPref(user, "mytasks"), [user]);
  const displayed = useMemo(() => applyViewPref(filtered, viewPref), [filtered, viewPref]);
  // Paginación 30+navegación solo para la vista Lista (Kanban/Timeline son tableros).
  const pag = usePaginacion(displayed);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const resolved = filtered.filter((t) => t.status === "completed").length;
    const now = new Date();
    const overdue = filtered.filter((t) => t.due_date && parseLocal(t.due_date) < now && t.status !== "completed").length;
    const overlapping = (() => {
      let count = 0;
      for (let i = 0; i < filtered.length; i++) {
        if (!filtered[i].start_date || !filtered[i].due_date || filtered[i].status === "completed") continue;
        for (let j = i + 1; j < filtered.length; j++) {
          if (!filtered[j].start_date || !filtered[j].due_date || filtered[j].status === "completed") continue;
          if (parseLocal(filtered[i].start_date) < parseLocal(filtered[j].due_date) && parseLocal(filtered[j].start_date) < parseLocal(filtered[i].due_date)) { count++; break; }
        }
      }
      return count;
    })();
    return { total, resolved, overdue, overlapping };
  }, [filtered]);

  // Projects that actually have tasks assigned to me (for the filter dropdown).
  const myProjects = useMemo(() => {
    const ids = new Set(tasks.filter((t) => t.project_id).map((t) => t.project_id));
    return projects.filter((p) => ids.has(p.id));
  }, [tasks, projects]);

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    push("Importando…", "info");
    try {
      const { data } = await api.post("/tasks/import-simple", fd, { headers: { "Content-Type": "multipart/form-data" } });
      push(`Importadas ${data.created} tareas${data.skipped ? ` (${data.skipped} omitidas)` : ""}`, "success");
      load();
    } catch (err) { push(err.response?.data?.detail || "Error al importar", "error"); }
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    const res = await api.get("/tasks/import-template.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "plantilla_tareas.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  // Deep-link from a notification: /?task=123
  useEffect(() => {
    const t = params.get("task");
    if (t) { setDetailId(Number(t)); params.delete("task"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  // Header "Crear" button
  useEffect(() => {
    const h = () => { setEditTask(null); setModalOpen(true); };
    window.addEventListener("tf:create-task", h);
    return () => window.removeEventListener("tf:create-task", h);
  }, []);

  useTaskSocket(user?.department_id ?? 0, (msg) => { if (msg.event?.startsWith("task.")) load(); });

  const changeStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); }
    catch (e) { push(e.response?.data?.detail || "No se pudo cambiar el estado", "error"); }
    load();
  };
  const openEdit = (t) => { setDetailId(null); setEditTask(t); setModalOpen(true); };

  return (
    // Pedido 2026-09-19: sin zoom — la página va al 100% para que tenga el
    // MISMO ancho que el resto (antes zoom:0.9 la encogía a ~90%, se veía más
    // angosta que las demás).
    <div>
      <PageHeader title="Mis Tareas" tone="tareas" subtitle={`${displayed.length} de ${tasks.length} tareas`}>
        <div className="flex items-center gap-3 text-xs">
          <span className="rounded-xl bg-brand-50 px-2.5 py-1 font-semibold text-brand-700">{kpis.total} total</span>
          <span className="rounded-xl bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">{kpis.resolved} resueltas</span>
          {kpis.overdue > 0 && <span className="rounded-xl bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">{kpis.overdue} vencidas</span>}
          {kpis.overlapping > 0 && <span className="rounded-xl bg-rose-50 px-2.5 py-1 font-semibold text-rose-700">{kpis.overlapping} solapadas</span>}
          {user?.last_login && <span className="text-slate-400" title="Última conexión">Ult. conexión: {fmtDate(user.last_login)}</span>}
        </div>
        <div className="relative">
          <Icon name="search" size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input id="tf-page-search" className="input w-52 pl-8" placeholder="Buscar por palabra…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className={`btn-ghost ${showFilters || activeFilters ? "bg-brand-50 text-brand-700" : ""}`} onClick={() => setShowFilters((s) => !s)}>
          <Icon name="filter" size={16} /> Filtros{activeFilters > 0 && <span className="ml-1 rounded-tag bg-brand-600 px-1.5 text-[11px] font-bold text-white">{activeFilters}</span>}
        </button>
        <ViewPrefsButton page="mytasks" />
        <ViewSwitcher view={view} setView={setView} />
      </PageHeader>

      {showFilters && (
        <div className="mx-auto flex w-[96%] max-w-pagina flex-wrap items-center gap-2 border-x border-b border-indigo-200 bg-indigo-50/40 px-6 py-3">
          <select className="input h-9 min-w-[6rem] flex-1 basis-28 text-sm" value={priorityF} onChange={(e) => setPriorityF(e.target.value)} title="Prioridad">
            <option value="">Prioridad</option>
            {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="input h-9 min-w-[6rem] flex-1 basis-28 text-sm" value={statusF} onChange={(e) => setStatusF(e.target.value)} title="Estado">
            <option value="">Estado</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {taskTypes.length > 0 && (
            <select className="input h-9 min-w-[6rem] flex-1 basis-28 text-sm capitalize" value={typeF} onChange={(e) => setTypeF(e.target.value)} title="Tipo de tarea">
              <option value="">Tipo</option>
              {taskTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {myProjects.length > 0 && (
            <select className="input h-9 min-w-[7rem] flex-1 basis-40 text-sm" value={projectF} onChange={(e) => setProjectF(e.target.value)} title="Proyecto">
              <option value="">Proyecto</option>
              {myProjects.map((p) => <option key={p.id} value={String(p.id)} className="uppercase">{p.name}</option>)}
            </select>
          )}
          <select className="input h-9 min-w-[5rem] flex-1 basis-28 text-sm" value={qualityF} onChange={(e) => setQualityF(e.target.value)} title="Calidad">
            <option value="">Calidad</option>
            <option value="yes">Verificado</option>
            <option value="no">No verificado</option>
          </select>
          <select className="input h-9 min-w-[7rem] flex-1 basis-40 text-sm" value={period?.from || ""}
            onChange={(e) => setPeriod(e.target.value ? periods.find((p) => p.from === e.target.value) : null)} title="Período (semana o mes)">
            <option value="">Período</option>
            <optgroup label="Semanas">
              {periods.filter((p) => p.group === "Semanas").map((p) => <option key={p.from} value={p.from}>Semana {p.label}</option>)}
            </optgroup>
            <optgroup label="Meses">
              {periods.filter((p) => p.group === "Meses").map((p) => <option key={p.from} value={p.from}>{p.label}</option>)}
            </optgroup>
          </select>
          <label className="flex h-9 items-center gap-1.5 whitespace-nowrap px-1 text-xs text-slate-600" title={`Ocultar tareas completadas hace más de ${user?.old_task_days || 45} días (ajustable en Mi Perfil). No aplica si el proyecto aún no está concluido.`}>
            <input type="checkbox" className="h-3.5 w-3.5 accent-brand-600" checked={hideOldDone} onChange={(e) => setHideOldDone(e.target.checked)} />
            Ocultar antiguas ({user?.old_task_days || 45}d)
          </label>
          {activeFilters > 0 && <button className="btn-ghost h-9 px-2 text-xs text-rose-600" onClick={() => { setPriorityF(""); setStatusF(""); setTypeF(""); setProjectF(""); setQualityF(""); setPeriod(null); }} title="Limpiar"><Icon name="x" size={14} /></button>}
        </div>
      )}
      {/* Contenido al 94% del ancho, centrado. */}
      <div className="pagina pb-6">
        {tasks.length === 0 ? (
          <div className="rounded-xl border border-indigo-300 bg-white">
            <EmptyState icon="list" title="No tienes tareas asignadas"
              hint="Crea una con el botón “+ Crear” de la barra superior, o impórtalas desde Excel."
              action={<button className="btn-primary" onClick={() => { setEditTask(null); setModalOpen(true); }}><Icon name="plus" size={15} /> Crear tarea</button>} />
          </div>
        ) : view === "kanban" ? (
          <KanbanBoard tasks={displayed} presorted onOpen={(t) => setDetailId(t.id)} onStatusChange={changeStatus} />
        ) : view === "list" ? (
          <>
            <TaskListView tasks={pag.slice} onOpen={(t) => setDetailId(t.id)} />
            <Paginacion pag={pag} />
          </>
        ) : (
          <div className="rounded-xl border border-indigo-300 bg-white p-4"><GanttTimeline groups={groupForTimeline(displayed)} zoom="week" onOpen={(id) => setDetailId(id)} /></div>
        )}
      </div>

      <TaskModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load}
        task={editTask} departments={departments} users={users} projects={projects}
        onProjectCreated={reloadProjects} lockDept={user?.department_id} defaultAssignee={user?.id} />
      <TaskDetailPanel taskId={detailId} open={Boolean(detailId)} onClose={() => setDetailId(null)} onChanged={load} onEdit={openEdit} />
    </div>
  );
}
